/**
 * 权限和路由管理
 *
 * 功能：
 * - 动态路由管理
 * - 权限路由过滤
 * - 菜单生成
 */
import { defineStore } from "pinia";
import { ref } from "vue";
import type { RouteRecordRaw } from "vue-router";
import { constantRoutes } from "@/router/constant-routes";
import { getAdminMenu } from "@/api/gen/admin-menu";
import type { AdminV1MenuTreeNode } from "@/api/gen/models";
import type { Component } from "vue";

// 预加载所有视图组件，支持多级目录的动态加载
const viewModules = (import.meta as any).glob("@/views/**/*.vue");

/**
 * 路由元信息接口
 */
export interface RouteMeta {
  title: string;
  icon?: string;
  hidden?: boolean;
  alwaysShow?: boolean;
  keepAlive?: boolean;
  affix?: boolean;
  activeMenu?: string;
  permissions?: string;
  isFrame?: boolean;
  frameSrc?: string;
}

export const usePermissionStore = defineStore("permission", () => {
  // 状态
  const routes = ref<RouteRecordRaw[]>([]);
  const dynamicRoutes = ref<RouteRecordRaw[]>([]);
  const menuRoutes = ref<RouteRecordRaw[]>([]);

  /**
   * 生成路由
   * @param roles 用户角色（暂未使用，权限控制由后端菜单接口处理）
   * @param permissions 用户权限列表（暂未使用，权限控制由后端菜单接口处理）
   */
  async function generateRoutes(roles: string[], permissions: string[]) {
    try {
      console.log("[Permission Store] 开始生成路由...");
      console.log("[Permission Store] 用户角色:", roles);
      console.log("[Permission Store] 用户权限:", permissions);

      // 从后端获取菜单树（后端已根据用户权限过滤）
      const menuAPI = getAdminMenu();
      const response = await menuAPI.getMenuTree({
        status: 1, // 只获取启用状态的菜单
      });

      console.log("[Permission Store] 菜单树 API 响应:", response);

      const menuTree = response?.items || [];

      console.log("[Permission Store] 菜单树节点数量:", menuTree.length);

      // 检查菜单树是否为空
      if (menuTree.length === 0) {
        console.error("[Permission Store] 错误：菜单树为空！");
        console.error("[Permission Store] 可能的原因：");
        console.error("  1. 数据库中没有菜单数据（需要运行 pnpm sync:menu）");
        console.error("  2. 用户没有任何权限");
        console.error("  3. 后端菜单 API 异常");
        throw new Error(
          "菜单数据为空，请联系管理员或运行 pnpm sync:menu 同步菜单"
        );
      }

      // 转换为 Vue Router 路由格式
      const accessRoutes = transformMenuToRoutes(menuTree);

      // 兜底追加抽奖计划相关路由，避免后端菜单未同步导致 404
      appendLotteryPlanFallback(accessRoutes, permissions);

      console.log("[Permission Store] 转换后的路由数量:", accessRoutes.length);
      console.log("[Permission Store] 转换后的路由:", accessRoutes);

      // 将动态路由添加到根路由的 children 中
      // 根路由使用布局组件
      const layoutRoute: RouteRecordRaw = {
        path: "/",
        component: () => import("@/layouts/index.vue"),
        redirect: "/dashboard/index",
        children: accessRoutes,
      };

      dynamicRoutes.value = [layoutRoute];

      // 合并固定路由和动态路由
      routes.value = [...constantRoutes, ...dynamicRoutes.value];

      // 生成菜单（过滤掉 hidden 的路由）
      // 仅保留具体业务路由用于侧边栏展示，避免出现占位根路由空白项
      menuRoutes.value = filterMenuRoutes(accessRoutes);

      console.log(
        "[Permission Store] 路由生成完成，动态路由数量:",
        dynamicRoutes.value.length
      );
      console.log("[Permission Store] 最终路由:", dynamicRoutes.value);

      return dynamicRoutes.value;
    } catch (error) {
      console.error("[Generate Routes Error]:", error);
      if (error instanceof Error) {
        console.error("[Generate Routes Error] 错误消息:", error.message);
        console.error("[Generate Routes Error] 错误堆栈:", error.stack);
      }
      throw error;
    }
  }

  /**
   * 兜底追加抽奖计划路由，避免未同步菜单时按钮跳 404
   * 仅当用户拥有对应权限时才追加
   */
  function appendLotteryPlanFallback(
    routes: RouteRecordRaw[],
    permissions: string[]
  ) {
    const hasPermission = (code: string) =>
      permissions.includes(code) || permissions.includes("*:*:*");

    const ensureRoute = (path: string, record: RouteRecordRaw) => {
      if (!routeExists(routes, path)) {
        routes.push(record);
      }
    };

    if (hasPermission("lottery:plan:list")) {
      ensureRoute("/lottery/plans", {
        path: "/lottery/plans",
        name: "LotteryPlanListLocal",
        component: () => import("@/views/lottery/plan/list.vue"),
        meta: {
          title: "menu.lottery_plan_list",
          permissions: "lottery:plan:list",
          keepAlive: true,
        },
      });
    }

    if (hasPermission("lottery:plan:view")) {
      ensureRoute("/lottery/plans/:id", {
        path: "/lottery/plans/:id",
        name: "LotteryPlanDetailLocal",
        component: () => import("@/views/lottery/plan/detail.vue"),
        meta: {
          title: "menu.lottery_plan_detail",
          hidden: true,
          permissions: "lottery:plan:view",
        },
      });
    }

    if (hasPermission("lottery:plan:create")) {
      ensureRoute("/lottery/plans/create", {
        path: "/lottery/plans/create",
        name: "LotteryPlanCreateLocal",
        component: () => import("@/views/lottery/plan/form.vue"),
        meta: {
          title: "menu.lottery_plan_create",
          hidden: true,
          permissions: "lottery:plan:create",
        },
      });
    }

    if (hasPermission("lottery:plan:update")) {
      ensureRoute("/lottery/plans/:id/edit", {
        path: "/lottery/plans/:id/edit",
        name: "LotteryPlanEditLocal",
        component: () => import("@/views/lottery/plan/form.vue"),
        meta: {
          title: "menu.lottery_plan_edit",
          hidden: true,
          permissions: "lottery:plan:update",
        },
      });
    }

    // 抽奖实例列表兜底
    if (hasPermission("lottery:lottery:list")) {
      ensureRoute("/lottery/lotteries", {
        path: "/lottery/lotteries",
        name: "LotteryListLocal",
        component: () => import("@/views/lottery/list.vue"),
        meta: {
          title: "menu.lottery_list",
          permissions: "lottery:lottery:list",
          keepAlive: true,
        },
      });
    }

    // 待开奖兜底
    if (hasPermission("lottery:draw:list")) {
      ensureRoute("/lottery/pending-draw", {
        path: "/lottery/pending-draw",
        name: "LotteryPendingDrawLocal",
        component: () => import("@/views/lottery/pending-draw.vue"),
        meta: {
          title: "menu.lottery_pending_draw",
          permissions: "lottery:draw:list",
          keepAlive: true,
        },
      });
    }

    // 中奖用户列表兜底
    if (hasPermission("lottery:winner:list")) {
      ensureRoute("/lottery/winners", {
        path: "/lottery/winners",
        name: "LotteryWinnerListLocal",
        component: () => import("@/views/lottery/winner/list.vue"),
        meta: {
          title: "menu.lottery_winner_list",
          permissions: "lottery:winner:list",
          keepAlive: true,
        },
      });
    }

    if (hasPermission("lottery:winner:view")) {
      ensureRoute("/lottery/winners/:id", {
        path: "/lottery/winners/:id",
        name: "LotteryWinnerDetailLocal",
        component: () => import("@/views/lottery/winner/detail.vue"),
        meta: {
          title: "menu.lottery_winner_detail",
          hidden: true,
          permissions: "lottery:winner:view",
        },
      });
    }

    // 参与记录列表兜底
    if (hasPermission("lottery:participation:list")) {
      ensureRoute("/lottery/participation", {
        path: "/lottery/participation",
        name: "LotteryParticipationLocal",
        component: () => import("@/views/lottery/participation.vue"),
        meta: {
          title: "menu.lottery_participation",
          permissions: "lottery:participation:list",
          keepAlive: true,
        },
      });
    }

    // 钱包模块兜底，避免菜单未同步导致跳转 404
    if (hasPermission("wallet:wallet:list")) {
      ensureRoute("/wallet/list", {
        path: "/wallet/list",
        name: "WalletListLocal",
        component: () => import("@/views/wallet/list.vue"),
        meta: {
          title: "menu.wallet_list",
          permissions: "wallet:wallet:list",
          keepAlive: true,
        },
      });
    }
    if (hasPermission("wallet:wallet:view")) {
      ensureRoute("/wallet/users/:userId", {
        path: "/wallet/users/:userId",
        name: "WalletDetailLocal",
        component: () => import("@/views/wallet/detail.vue"),
        meta: {
          title: "menu.wallet_detail",
          permissions: "wallet:wallet:view",
          hidden: true,
        },
      });
    }
    if (hasPermission("wallet:ledger:list")) {
      ensureRoute("/wallet/ledger", {
        path: "/wallet/ledger",
        name: "WalletLedgerLocal",
        component: () => import("@/views/wallet/ledger-list.vue"),
        meta: {
          title: "menu.wallet_ledger",
          permissions: "wallet:ledger:list",
          keepAlive: true,
        },
      });
    }
    if (hasPermission("wallet:ledger:view")) {
      ensureRoute("/wallet/ledger/:id", {
        path: "/wallet/ledger/:id",
        name: "WalletLedgerDetailLocal",
        component: () => import("@/views/wallet/ledger-detail.vue"),
        meta: {
          title: "menu.wallet_ledger_detail",
          permissions: "wallet:ledger:view",
          hidden: true,
        },
      });
    }
    if (hasPermission("wallet:reconciliation:list")) {
      ensureRoute("/wallet/reconciliation/runs", {
        path: "/wallet/reconciliation/runs",
        name: "WalletReconciliationRunsLocal",
        component: () => import("@/views/wallet/reconciliation-runs.vue"),
        meta: {
          title: "menu.wallet_recon_runs",
          permissions: "wallet:reconciliation:list",
          keepAlive: true,
        },
      });
      ensureRoute("/wallet/reconciliation/logs", {
        path: "/wallet/reconciliation/logs",
        name: "WalletReconciliationLogsLocal",
        component: () => import("@/views/wallet/reconciliation-logs.vue"),
        meta: {
          title: "menu.wallet_recon_logs",
          permissions: "wallet:reconciliation:list",
          keepAlive: true,
        },
      });
    }
    if (hasPermission("wallet:reconciliation:view")) {
      ensureRoute("/wallet/reconciliation/runs/:id", {
        path: "/wallet/reconciliation/runs/:id",
        name: "WalletReconciliationRunDetailLocal",
        component: () => import("@/views/wallet/reconciliation-run-detail.vue"),
        meta: {
          title: "menu.wallet_recon_runs",
          permissions: "wallet:reconciliation:view",
          hidden: true,
        },
      });
    }
  }

  /**
   * 转换菜单树为 Vue Router 路由
   * @param menuNodes 后端菜单树节点数组
   */
  function transformMenuToRoutes(
    menuNodes: AdminV1MenuTreeNode[]
  ): RouteRecordRaw[] {
    const routes: RouteRecordRaw[] = [];

    menuNodes.forEach((node) => {
      // 只处理状态为启用(1)的菜单
      if (node.status !== 1) {
        return;
      }

      // 跳过按钮类型的菜单（type = 3），只处理目录(1)和菜单(2)
      if (node.type === 3) {
        return;
      }

      const route: RouteRecordRaw = {
        path: node.path || "",
        name: node.name || "",
        component: loadComponent(node.component, node.isFrame === 1),
        redirect: node.redirect,
        meta: {
          title: node.title || "",
          icon: node.icon,
          hidden: node.hidden === 1,
          alwaysShow: node.alwaysShow === 1,
          keepAlive: node.keepAlive === 1,
          affix: node.affix === 1,
          activeMenu: node.activeMenu,
          permissions: node.permissions,
          isFrame: node.isFrame === 1,
          frameSrc: node.frameSrc,
        },
      } as any;

      // 处理子菜单
      if (node.children && node.children.length > 0) {
        route.children = transformMenuToRoutes(node.children);
      }

      routes.push(route);
    });

    return routes;
  }

  /**
   * 路由是否已存在（递归搜索）
   */
  function routeExists(routes: RouteRecordRaw[], path: string): boolean {
    return routes.some((route) => {
      if (route.path === path) return true;
      if (route.children) {
        return routeExists(route.children, path);
      }
      return false;
    });
  }

  /**
   * 动态加载组件
   * @param componentPath 组件路径
   * @param isFrame 是否为 iframe
   */
  function loadComponent(componentPath?: string, isFrame = false) {
    // 如果是 iframe，返回 iframe 组件
    if (isFrame) {
      return () => import("@/views/iframe/index.vue");
    }

    // 如果没有指定组件路径，返回空的路由视图（用于父级菜单）
    if (!componentPath) {
      return () => import("@/layouts/components/RouteView.vue");
    }

    // 动态导入视图组件（支持多级目录）
    // 组件路径格式: "system/user/index" -> "@/views/system/user/index.vue"
    const normalizedPath = componentPath.replace(/^\//, "");
    const matchedEntry = Object.entries(viewModules).find(([key]) =>
      key.endsWith(`/${normalizedPath}.vue`)
    );

    if (matchedEntry) {
      return matchedEntry[1] as () => Promise<Component>;
    }

    console.warn("[Permission Store] 未找到视图组件:", componentPath);
    return () => import("@/views/error/404.vue");
  }

  /**
   * 过滤菜单路由（移除 hidden 的路由）
   */
  function filterMenuRoutes(routes: RouteRecordRaw[]): RouteRecordRaw[] {
    return routes.filter((route) => {
      if (route.meta?.hidden) {
        return false;
      }
      if (route.children) {
        route.children = filterMenuRoutes(route.children);
      }
      return true;
    });
  }

  /**
   * 重置路由
   */
  function resetRoutes() {
    routes.value = [];
    dynamicRoutes.value = [];
    menuRoutes.value = [];
  }

  return {
    routes,
    dynamicRoutes,
    menuRoutes,
    generateRoutes,
    resetRoutes,
  };
});
